import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UtensilsCrossed, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@hotel.ch");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/17057034/pexels-photo-17057034.jpeg"
          alt="Restaurant"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#5E2129]/70" />
        <div className="absolute inset-0 flex flex-col justify-end p-14 text-[#FDFBF7]">
          <UtensilsCrossed className="w-12 h-12 text-[#C28E3A] mb-6" />
          <h2 className="font-serif text-5xl leading-tight mb-4">Du ticket<br />à la facture,<br />en un instant.</h2>
          <p className="text-[#FDFBF7]/80 max-w-md">
            Photographiez le ticket de caisse, laissez la reconnaissance faire le reste,
            validez et envoyez. Synchronisé avec Bexio.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFBF7]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#5E2129] flex items-center justify-center">
              <UtensilsCrossed className="w-6 h-6 text-[#C28E3A]" />
            </div>
            <span className="font-serif text-2xl text-[#5E2129]">DuPeyrou</span>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-3">Espace gérant</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mb-2">Bienvenue</h1>
          <p className="text-[#666] mb-8">Connectez-vous pour gérer vos factures.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#1A1A1A]">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-white border-[#EBE5D9] focus:ring-[#5E2129]/20 focus:border-[#5E2129] h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1A1A1A]">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white border-[#EBE5D9] focus:ring-[#5E2129]/20 focus:border-[#5E2129] h-12"
                required
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white font-semibold transition-colors duration-200"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Se connecter"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
